<template>
  <div class="od-tag-input-shell" v-click-outside="closeSuggestions">
    <div class="od-tag-input" :class="{ 'od-tag-input--has-flash': !!flashError }" @click="focusInput">
      <span
        v-for="(t, i) in tags"
        :key="t + '-' + i"
        :class="pillClass"
      >
        {{ tagDisplay(t) }}
        <button
          type="button"
          class="od-tag-input-x"
          :aria-label="'remove ' + t"
          @click.stop="removeAt(i)"
        >&times;</button>
      </span>
      <input
        ref="input"
        type="text"
        class="od-tag-input-field"
        :value="draft"
        :placeholder="placeholder"
        @input="onInput"
        @keydown="onKeydown"
        @focus="onFocus"
        @blur="onBlur"
      />
      <span v-if="flashError" class="od-tag-input-error">{{ flashError }}</span>
    </div>

    <!-- typeahead dropdown. only renders when there's a draft AND
         matching suggestions to surface. positioned absolutely so it
         floats over the surrounding layout without affecting flow. -->
    <ul
      v-if="suggestionOpen && filteredSuggestions.length"
      class="od-tag-input-suggest"
      role="listbox"
    >
      <li
        v-for="(s, idx) in filteredSuggestions"
        :key="s.value + '-' + idx"
        :class="['od-tag-input-suggest-item', { 'od-tag-input-suggest-item--active': idx === highlightIndex }]"
        role="option"
        :aria-selected="idx === highlightIndex"
        @mousedown.prevent="onPickSuggestion(s)"
        @mouseenter="highlightIndex = idx"
      >
        <div class="od-tag-input-suggest-row1">
          <span class="od-tag-input-suggest-main">{{ s.label || s.value }}</span>
          <span v-if="s.chip" class="od-tag-input-suggest-chip">{{ s.chip }}</span>
        </div>
        <div v-if="s.sublabel" class="od-tag-input-suggest-sub">{{ s.sublabel }}</div>
      </li>
    </ul>
  </div>
</template>

<script>
// local replacement for BFormTags. the project ships bootstrap-vue
// 2.0.0-rc.28, which predates the b-form-tags component (added in
// BV 2.2.0). v-model + separator + validator shape, plus an optional
// typeahead suggestions list that filters as the user types.
export default {
  name: 'od_tag_input',
  props: {
    value: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'add and press enter...' },
    separator: { type: String, default: ' ,;' },
    variant: { type: String, default: 'primary' }, // primary | secondary | info
    validator: { type: Function, default: null },
    invalidText: { type: String, default: 'Not valid' },
    duplicateText: { type: String, default: 'Already in list' },
    // typeahead options. when present, the input shows a filtered
    // dropdown of matching candidates. each entry is either a plain
    // string (value + label both) or an object { value, label, sublabel }.
    // value is the thing added to the tag list when picked.
    suggestions: { type: Array, default: null },
    // restrict-to-suggestions: when true, Enter / separator only accepts
    // a value if it appears in the suggestions list. used for fields where
    // the candidate set is closed (e.g. tenant usernames).
    restrictToSuggestions: { type: Boolean, default: false },
    // max suggestions shown in the dropdown.
    maxSuggestions: { type: Number, default: 8 },
  },
  data() {
    return {
      draft: '',
      flashError: '',
      suggestionOpen: false,
      highlightIndex: 0,
      blurTimer: null,
    }
  },
  computed: {
    sepChars() {
      return new Set(Array.from(this.separator || ''))
    },
    tags() {
      return Array.isArray(this.value) ? this.value : []
    },
    pillClass() {
      const v = this.variant || 'primary'
      return 'od-tag-input-pill od-tag-input-pill--' + v
    },
    // normalize suggestions into {value, label, sublabel}. accepts a plain
    // string array as a shortcut.
    normalizedSuggestions() {
      const raw = Array.isArray(this.suggestions) ? this.suggestions : []
      return raw.map(s => {
        if (typeof s === 'string') return { value: s, label: s }
        return {
          value: s.value,
          label: s.label || s.value,
          sublabel: s.sublabel || '',
          chip: s.chip || '',
        }
      }).filter(s => !!s.value)
    },
    // filtered, deduped against already-selected tags, capped at maxSuggestions.
    filteredSuggestions() {
      const q = (this.draft || '').trim().toLowerCase()
      const taken = new Set(this.tags.map(t => (t || '').toLowerCase()))
      const out = []
      for (const s of this.normalizedSuggestions) {
        const v = (s.value || '').toLowerCase()
        if (taken.has(v)) continue
        if (q) {
          const hay = ((s.value || '') + ' ' + (s.label || '') + ' ' + (s.sublabel || '') + ' ' + (s.chip || '')).toLowerCase()
          if (hay.indexOf(q) === -1) continue
        }
        out.push(s)
        if (out.length >= this.maxSuggestions) break
      }
      return out
    },
  },
  methods: {
    onInput(e) {
      const raw = e.target.value
      if (raw.length > 0 && this.sepChars.has(raw[raw.length - 1])) {
        const value = raw.slice(0, -1).trim()
        this.tryAdd(value)
        this.draft = ''
        e.target.value = ''
      } else {
        this.draft = raw
      }
      this.suggestionOpen = true
      this.highlightIndex = 0
    },
    onKeydown(e) {
      if (e.key === 'ArrowDown') {
        if (this.filteredSuggestions.length) {
          e.preventDefault()
          this.suggestionOpen = true
          this.highlightIndex = Math.min(this.highlightIndex + 1, this.filteredSuggestions.length - 1)
        }
      } else if (e.key === 'ArrowUp') {
        if (this.filteredSuggestions.length) {
          e.preventDefault()
          this.highlightIndex = Math.max(this.highlightIndex - 1, 0)
        }
      } else if (e.key === 'Escape') {
        this.suggestionOpen = false
      } else if (e.key === 'Enter') {
        e.preventDefault()
        // if the dropdown is open and there's a highlighted suggestion,
        // pick it. otherwise fall through to tryAdd with the draft text.
        if (this.suggestionOpen && this.filteredSuggestions.length) {
          const picked = this.filteredSuggestions[this.highlightIndex] || this.filteredSuggestions[0]
          this.onPickSuggestion(picked)
        } else {
          this.tryAdd(this.draft.trim())
          this.draft = ''
          e.target.value = ''
        }
      } else if (e.key === 'Backspace' && !this.draft && this.tags.length) {
        this.removeAt(this.tags.length - 1)
      }
    },
    onPickSuggestion(s) {
      if (!s) return
      this.tryAdd(s.value)
      this.draft = ''
      const el = this.$refs.input
      if (el) el.value = ''
      this.suggestionOpen = false
      this.highlightIndex = 0
    },
    tryAdd(value) {
      if (!value) return
      if (this.tags.includes(value)) {
        this.flashError = this.duplicateText
        this.scheduleClear()
        return
      }
      if (this.restrictToSuggestions) {
        const inList = this.normalizedSuggestions.some(s => s.value === value)
        if (!inList) {
          this.flashError = this.invalidText
          this.scheduleClear()
          return
        }
      }
      if (this.validator && !this.validator(value)) {
        this.flashError = this.invalidText
        this.scheduleClear()
        return
      }
      this.flashError = ''
      this.$emit('input', [...this.tags, value])
    },
    removeAt(i) {
      const next = this.tags.slice()
      next.splice(i, 1)
      this.$emit('input', next)
    },
    scheduleClear() {
      setTimeout(() => { this.flashError = '' }, 2200)
    },
    focusInput() {
      this.$nextTick(() => {
        const el = this.$refs.input
        if (el) el.focus()
      })
    },
    onFocus() {
      this.suggestionOpen = true
    },
    onBlur() {
      // delay so a click on a suggestion can fire before the dropdown
      // closes. mousedown is preventDefault'd which keeps focus, but a
      // touch tap on mobile can still race.
      this.blurTimer = setTimeout(() => {
        this.suggestionOpen = false
      }, 120)
    },
    closeSuggestions() {
      this.suggestionOpen = false
    },
    // map a tag value to its display label via suggestions, so picked
    // emails / usernames render with their human-friendly names.
    tagDisplay(v) {
      const s = this.normalizedSuggestions.find(x => x.value === v)
      return (s && s.label) || v
    },
  },
  // lightweight click-outside directive — registered locally so the
  // component doesn't depend on a plugin install.
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._odClickOutside = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.addEventListener('mousedown', el._odClickOutside)
      },
      unbind(el) {
        document.removeEventListener('mousedown', el._odClickOutside)
        delete el._odClickOutside
      },
    },
  },
}
</script>

<style scoped>
/* outer shell hosts the absolutely positioned suggestion dropdown so
   the input itself can stay a clean flex row. */
.od-tag-input-shell {
  position: relative;
  max-width: 640px;
}

/* pill row that wraps, with an inline input at the tail. click anywhere
   inside focuses the input so the whole control reads as one editable
   field. focus-within ring matches the b-form-input look. */
.od-tag-input {
  display: flex; flex-wrap: wrap; gap: 6px;
  background: #fff; border: 1px solid #c8d4df; border-radius: 4px;
  padding: 5px 8px; min-height: 36px;
  font-size: 13px; color: #1d1d1f;
  cursor: text;
}
.od-tag-input:focus-within {
  border-color: var(--fi-rail-accent);
  box-shadow: 0 0 0 2px rgba(70, 130, 180, 0.15);
}
.od-tag-input--has-flash { border-color: #b91c1c; }
.od-tag-input-field {
  flex: 1 1 120px; min-width: 120px;
  border: none; outline: none; background: transparent;
  font-size: 13px; color: #1d1d1f;
  padding: 2px 0;
}
.od-tag-input-field::placeholder { color: #9ca3af; }

.od-tag-input-pill {
  display: inline-flex; align-items: center; gap: 4px;
  border-radius: 999px;
  padding: 1px 6px 1px 10px;
  font-size: 12px; font-weight: 600; line-height: 1.5;
}
.od-tag-input-pill--primary { background: #e8f1fa; color: #1f5582; }
.od-tag-input-pill--secondary { background: #f1f3f5; color: #4b5563; }
.od-tag-input-pill--info { background: #e0f2fe; color: #075985; }

.od-tag-input-x {
  background: transparent; border: none; padding: 0;
  font-size: 16px; line-height: 1; color: inherit;
  opacity: 0.55; cursor: pointer;
  margin-left: 2px;
}
.od-tag-input-x:hover { opacity: 1; }

.od-tag-input-error {
  flex: 0 0 100%;
  color: #b91c1c; font-size: 12px;
  padding: 2px 0 0 0;
}

/* suggestions dropdown: floats below the input. capped height so a
   large candidate set scrolls instead of dominating the page. */
.od-tag-input-suggest {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 50;
  background: #fff;
  border: 1px solid #c8d4df;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(15, 31, 50, 0.08);
  padding: 4px 0;
  margin: 0;
  list-style: none;
  max-height: 220px;
  overflow-y: auto;
}
.od-tag-input-suggest-item {
  padding: 6px 12px;
  cursor: pointer;
  border-left: 2px solid transparent;
}
.od-tag-input-suggest-item--active,
.od-tag-input-suggest-item:hover {
  background: #eef4fb;
  border-left-color: var(--fi-rail-accent);
}
.od-tag-input-suggest-row1 {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.od-tag-input-suggest-main {
  font-size: 13px; color: #1d1d1f;
  font-weight: 500;
}
.od-tag-input-suggest-chip {
  flex: 0 0 auto;
  font-size: 10.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.03em;
  color: var(--fi-ops-eyebrow); background: #eef2f6; border: 1px solid #dde4ec;
  border-radius: 999px; padding: 1px 7px;
}
.od-tag-input-suggest-sub {
  font-size: 11.5px; color: #6b7280;
  margin-top: 1px;
}
</style>
